/** App-wide constants shared by the web app and the API. */

export const APP_NAME = 'LessAlone';

/**
 * Age policy. The platform is strictly 18+. The floor is upheld by
 * community attestation — each community confirms its email-domain
 * roster is adults-only before being added to the platform. There is
 * no per-user age screen at runtime. The constant remains for any
 * downstream copy that wants to surface the policy ("LessAlone is
 * for adults 18+").
 */
export const MINIMUM_AGE = 18;

/** Version tag stamped on every recorded consent acknowledgment. */
export const CONSENT_VERSION = '2026-05-01';

/**
 * Version tag for the peer agreement (BeFriender-style: listen, keep private,
 * refer to 988 in crisis). Bumping the version invalidates prior acceptances
 * and re-opens the modal — use that when the wording changes materially.
 */
export const PAIRING_AGREEMENT_VERSION = '2026-05-21';

/**
 * Generic crisis run-book. Surfaced to a moderator inside a crisis
 * event when the community.crisis_runbook column is NULL. Plain text
 * with line breaks; the UI renders it inside a <pre>-styled block.
 *
 * A community can override this by setting community.crisis_runbook
 * (founder dashboard → onboard-a-community form, or via SQL post-hoc)
 * — the override carries institution-specific instructions (campus
 * oncall number, BIT report form, EAP intake link).
 */
export const CRISIS_RUNBOOK_DEFAULT = `When a crisis event lands:

1. Read the message that triggered it. The user already saw 988 +
   Crisis Text Line — your job is the next layer, not the immediate
   referral.

2. Decide if the situation requires escalation to a real-world
   contact:
   • Active intent or plan disclosed → escalate to the community's
     crisis_escalation contact (shown above).
   • Past ideation / venting / generalized distress → reach out via
     in-app message acknowledging the moderator review and pointing
     them at the community's resources.

3. Record the action in the resolution note. Every step lands in the
   append-only audit log; future you will thank past you.

4. Take care of yourself. You don't have to act alone — escalate to
   another moderator or to the founder if the situation is heavy.`;

/**
 * Crisis resources, surfaced to every user unconditionally and never gated.
 * The product detects and refers — it never treats. See
 * docs/moderation-and-crisis.md.
 */
export const CRISIS_RESOURCES = [
  {
    name: '988 Suicide & Crisis Lifeline',
    detail: 'Call or text 988',
    href: 'tel:988',
    url: 'https://988lifeline.org',
  },
  {
    name: 'Crisis Text Line',
    detail: 'Text HOME to 741741',
    href: 'sms:741741',
    url: 'https://www.crisistextline.org',
  },
] as const;

/**
 * The UCLA 3-item loneliness scale (Hughes et al., 2004) — the pilot's
 * outcome measure (docs/launch-and-liquidity.md). Each item scores 1–3; the
 * total runs 3 (least lonely) to 9 (most lonely).
 */
export const LONELINESS_QUESTIONS = [
  'How often do you feel that you lack companionship?',
  'How often do you feel left out?',
  'How often do you feel isolated from others?',
] as const;

export const LONELINESS_OPTIONS = [
  { label: 'Hardly ever', value: 1 },
  { label: 'Some of the time', value: 2 },
  { label: 'Often', value: 3 },
] as const;

/**
 * Curated interest-tags for onboarding — broad enough to cover most
 * student / early-career interests; users may also add custom tags
 * subject to the nickname policy (no slurs / sexual / contact info).
 * Tags weight matching but never wall anyone off into a clique. See
 * docs/community-architecture.md.
 */
export const INTEREST_TAGS = [
  // Academics + early career
  'nursing',
  'computer-science',
  'engineering',
  'business',
  'pre-med',
  'pre-law',
  'education',
  'psychology',
  'biology',
  'history',
  'philosophy',
  'design',
  // Identity + life-stage
  'first-gen',
  'transfer',
  'commuter',
  'international',
  'veteran',
  'parent',
  'lgbtq',
  'faith',
  'mental-health',
  // Creative + cultural
  'art',
  'music',
  'writing',
  'photography',
  'dance',
  'theater',
  'film',
  'languages',
  'reading',
  // Active + outdoors
  'hiking',
  'running',
  'cycling',
  'climbing',
  'sports',
  'athletics',
  'yoga',
  // Hobbies + interests
  'gaming',
  'plants',
  'cooking',
  'baking',
  'volunteering',
  'travel',
  'fashion',
  'crafts',
  'anime',
  'podcasts',
  'pets',
  'coffee',
] as const;

export const MAX_INTEREST_TAGS = 5;
